import React from "react";
import "@/App.css";
import "@/i18n";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute, PublicOnlyRoute, RequireOrganization, RequirePlatformAdmin } from "@/components/auth/guards";
import PlatformAdminShell from "@/pages/admin/PlatformAdminShell";
import PlatformAdminLandingPage from "@/pages/admin/PlatformAdminLandingPage";
import PlatformOrganizationsPage from "@/pages/admin/PlatformOrganizationsPage";
import PlatformOrganizationDetailPage from "@/pages/admin/PlatformOrganizationDetailPage";
import PlatformUsersPage from "@/pages/admin/PlatformUsersPage";
import PlatformObservabilityPage from "@/pages/admin/PlatformObservabilityPage";
import PlatformPlansPage from "@/pages/admin/PlatformPlansPage";
import { AppShell } from "@/components/layout/AppShell";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import OnboardingPage from "@/pages/onboarding/OnboardingPage";
import DashboardPage from "@/pages/DashboardPage";
import BrandBrainPage from "@/pages/brand-brain/BrandBrainPage";
import SettingsPage from "@/pages/settings/SettingsPage";
import AssistantPage from "@/pages/assistant/AssistantPage";
import AIOperationsPage from "@/pages/ai/AIOperationsPage";
import AgentsPage from "@/pages/agents/AgentsPage";
import CRMPage from "@/pages/crm/CRMPage";
import ApprovalsPage from "@/pages/approvals/ApprovalsPage";
import ContentCalendarPage from "@/pages/social/ContentCalendarPage";
import ComingSoonPage from "@/pages/ComingSoonPage";
import NotFoundPage from "@/pages/NotFoundPage";
import { COMING_SOON_ITEMS } from "@/lib/nav";

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <TooltipProvider>
            <Routes>
              <Route path="/" element={<Navigate to="/app" replace />} />

              <Route element={<PublicOnlyRoute />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
              </Route>
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              <Route element={<ProtectedRoute />}>
                <Route path="/onboarding" element={<OnboardingPage />} />
                <Route element={<RequirePlatformAdmin />}>
                  <Route path="/app/admin" element={<PlatformAdminShell />}>
                    <Route index element={<PlatformAdminLandingPage />} />
                    <Route path="organizations" element={<PlatformOrganizationsPage />} />
                    <Route path="organizations/:orgId" element={<PlatformOrganizationDetailPage />} />
                    <Route path="users" element={<PlatformUsersPage />} />
                     <Route path="observability" element={<PlatformObservabilityPage />} />
                     <Route path="plans" element={<PlatformPlansPage />} />
                  </Route>
                </Route>
                <Route element={<RequireOrganization />}>
                  <Route path="/app" element={<AppShell />}>
                    <Route index element={<DashboardPage />} />
                    <Route path="assistant" element={<AssistantPage />} />
                    <Route path="ai" element={<AIOperationsPage />} />
                    <Route path="agents" element={<AgentsPage />} />
                    <Route path="agents/:agentId" element={<AgentsPage />} />
                    <Route path="brand-brain" element={<BrandBrainPage />} />
                    <Route path="settings" element={<SettingsPage />} />
                    <Route path="crm" element={<CRMPage />} />
                    <Route path="approvals" element={<ApprovalsPage />} />
                    <Route path="social" element={<ContentCalendarPage />} />
                    {COMING_SOON_ITEMS.map((item) => (
                      <Route key={item.key} path={item.path.replace("/app/", "")} element={<ComingSoonPage moduleKey={item.key} />} />
                    ))}
                  </Route>
                </Route>
              </Route>

              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            <Toaster position="top-right" theme="light" richColors={false} closeButton visibleToasts={3} />
          </TooltipProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
